import { flatMap, isEqual, sortBy } from "lodash";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { isPathnameRestricted } from "@app/authorization";
import { ExportType } from "@app/exports/enumerations";
import { ProjectRouteParams } from "@app/projects/projectRouteParams";
import { RegionGranularity } from "@app/region-sets";
import Pie from "@app/shared/pie";
import { ProgressBarBand } from "@app/shared/progressBarBand";
import { Panel as SkeletonPanel, PrimaryTable as SkeletonTable } from "@app/shared/skeleton-components";
import { ColumnConfig, ColumnGroup, numberSort, SortDirections, TextAlignment, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { AccuracyTemplate, AccuracyTemplateInspection, AccuracyTemplateInspectionGroup, AccuracyTemplateScoringBand } from "@app/templates/accuracy/models";
import InformationalIndicator from "@app/projects/network-assessment/InformationalSpecialtyGroupIndicator";
import { formatHelper } from "@app/projects/network-assessment/formatHelper";
import { AttestationRateDays, FilterType, ProjectPerspective } from "@app/projects/enumerators";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { JSX } from "react";
import StateName from "@app/projects/network-assessment/stateName";
import Features from "@app/feature-flags/features";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { UserContextProps, withUserContext } from "@app/user/UserContext";
import { AccuracyAggregateInspectionResultGroup, NumericResultType } from "@app/projects/results/accuracy/accuracyAggregateInspectionResultGroup";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import AccuracyNetworkProvidersPanel from "@app/projects/servicing-provider/accuracy/AccuracyNetworkProvidersPanel";
import { AccuracyProviderProfileFromQuery } from "@app/projects/servicing-provider/accuracy/AccuracyProviderProfileFromQuery";
import { AccuracyTemplateContext } from "@app/templates/accuracy";
import { filterDirectoryFacingInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyDirectoryFacingFilter";
import { filterPractitionerAtLocationConfidenceInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyPractitionerAtLocationConfidenceFilter";
import { filterProviderClaimsActivityInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyProviderClaimsActivityFilter";
import AccuracyTooltipLegend from "@app/projects/network-assessment/accuracy/AccuracyTooltipLegend";
import { AccuracyDetail } from "@app/projects/network-assessment/accuracy/accuracyDetail";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

type AccuracyDetailsProps = RouteComponentProps<unknown, unknown, ProjectRouteParams> & CountyProjectContextProps & UserContextProps & FeatureFlagContextProps & {
  exportEndpoint: string;
};

class AccuracyDetailWaffleToppings {
  public title: JSX.Element;
  public key: string;
  public providers: React.ReactNode;
  public score: React.ReactNode;
  declare public recentlyAttestedPercent: React.ReactNode;

  constructor(key: string, providers: React.ReactNode, score: React.ReactNode, title: JSX.Element, inspections: InspectionNode[], recentlyAttestedPercent: React.ReactNode) {
    this.title = title;
    this.key = key;
    this.providers = providers;
    this.score = score;

    for (const inspection of inspections) {
      Object.assign(this, { [inspection.inspectionUID]: inspection.element });
    }

    this.recentlyAttestedPercent = recentlyAttestedPercent;
  }
}

class InspectionNode {
  public element: React.ReactNode;
  public inspectionUID: string;

  constructor(inspectionUID: string, element: React.ReactNode) {
    this.element = element;
    this.inspectionUID = inspectionUID;
  }
}

interface AccuracyDetailsState {
  loading: boolean;
  selectedRecords: AccuracyDetail[];
  selectedSortingColor: string;
  skip: number;
  sortedColumn: keyof AccuracyDetailWaffleToppings;
  sortedRecords: AccuracyDetail[];
  totalAggregateCount?: number;
}

class AccuracyDetails extends React.Component<AccuracyDetailsProps, AccuracyDetailsState> {
  private bands: AccuracyTemplateScoringBand[] = [];
  private inspectionGroups: AccuracyTemplateInspectionGroup[] = [];
  private sortingColorMap: Map<string, string[]> = new Map<string, string[]>();
  private recordsPerPage = 100;

  constructor(props: AccuracyDetailsProps) {
    super(props);

    this.state = {
      loading: false,
      selectedRecords: [],
      selectedSortingColor: null,
      skip: 0,
      sortedColumn: null,
      sortedRecords: sortBy(this.props.accuracyResult.details, ["defaultDisplayOrder"]),
    };
  }

  public async componentDidMount() {
    await this.fetchRecords();

    if (this.props.location.state && this.props.location.state.runConfiguration) {
      const config = this.props.location.state.runConfiguration;
      const compareToConfig = this.props.location.state.compareToRunConfiguration;
      await this.props.setDualMembershipMode(this.props.location.state.isDualMembershipMode, config.projectRunConfigurationType, config.projectRunConfigurationId, compareToConfig ? compareToConfig.projectRunConfigurationId : undefined);
    }
  }

  public async componentDidUpdate(prevProps: AccuracyDetailsProps) {
    if (!isEqual(prevProps.accuracyResult.details, this.props.accuracyResult.details)) {
      this.setState({
        sortedRecords: sortBy(this.props.accuracyResult.details, ["defaultDisplayOrder"]),
      });
    }

    // Wipe the selections on filter change, to avoid referencing now-missing record
    if (!isEqual(prevProps.regionFilters, this.props.regionFilters)) {
      await this.fetchRecords();
      this.setState({
        selectedRecords: [],
      });
    }
  }

  public render() {
    const haveResultsToRender = !this.props.loadingResults && !!this.props.runConfiguration && !!this.props.accuracyResult && !!this.props.accuracyResult.details;

    let countyRegionCode: string = null;
    let specialtyGroupCode: string = null;

    if (this.props.regionFilters) {
      countyRegionCode = this.props.regionFilters.county;
      specialtyGroupCode = this.props.regionFilters.specialty;
    }

    const isLowestGranularity = !!countyRegionCode && !!specialtyGroupCode;

    if (haveResultsToRender && this.sortingColorMap.size === 0) {
      this.buildSortingColorMaps();
    }

    return (
      <AccuracyTemplateContext.Consumer>
        {(accuracyTemplate) => (
          <>
            {(!haveResultsToRender || null) && (<SkeletonResults isLowestGranularity={isLowestGranularity} runConfiguration={this.props.runConfiguration || this.props.compareToRunConfiguration} />)}
            {(haveResultsToRender || null) && (this.renderResults(isLowestGranularity, accuracyTemplate))}
          </>
        )}
      </AccuracyTemplateContext.Consumer>
    );
  }

  private buildSortingColorMaps = () => {
    // in order to avoid allowing the user to sort using colors that are not available,
    // only give the color options for which there is corresponding data for
    // inspection pie
    for (const aggregateDetail of this.state.sortedRecords) {
      if (aggregateDetail.resultGroups !== undefined) {
        for (const resultGroup of aggregateDetail.resultGroups) {
          if (resultGroup.results !== undefined) {
            for (const result of resultGroup.results) {
              const existingMapping = this.sortingColorMap.get(result.inspectionUid);

              // if a map for a particular result group has already been initialized,
              // add to said color map, rather than completely overwriting it
              if (existingMapping) {
                for (const band of result.bandAggregates) {
                  if (!existingMapping.includes(band.color)) {
                    existingMapping.push(band.color);
                  }
                }

                this.sortingColorMap.set(result.inspectionUid, existingMapping);
              } else {
                const bandColors: string[] = [];

                for (const band of result.bandAggregates) {
                  bandColors.push(band.color);
                }

                this.sortingColorMap.set(result.inspectionUid, bandColors);
              }
            }
          }
        }
      }
    }
  }

  private renderResults = (isLowestGranularity: boolean, accuracyTemplate: AccuracyTemplate) => {
    this.storeInspectionsAndBands(accuracyTemplate);

    const paginationProps: WafflesPaginationProps = {
      changePage: (newPageNumber) => {
        this.setState({
          loading: true,
        });
        this.onPageChange(newPageNumber);
      },
      recordsPerPage: this.recordsPerPage,
      skip: this.state.skip,
      totalRecords: this.state.totalAggregateCount,
    };

    const record = isLowestGranularity
      ? this.props.accuracyResult.details[0]
      : null;

    return (
      <>
        <Waffles<AccuracyDetailWaffleToppings>
          bulkActionBar={this.getBulkActionBar()}
          columns={this.getColumnConfig(accuracyTemplate, isLowestGranularity)}
          columnGroups={this.getColumnGroups(accuracyTemplate, this.props.attestationRateDays)}
          customSort={this.customSort}
          data={this.buildWaffleToppings(this.state.sortedRecords.filter(sr => sr.resultGroups !== undefined), accuracyTemplate) || []}
          fullWidth={true}
          handleCheckboxChange={this.handleCheckboxChange}
          loading={this.state.loading}
          paginationProps={paginationProps}
          enablePagination={this.props.enabledFeatureFlags.has(Features.ACCURACY_LARGE_PROJECT_PERFORMANCE_ENHANCEMENTS) && !isLowestGranularity}
          selectedKeys={this.state.selectedRecords.map((sr: AccuracyDetail) => sr.key)}
          showCheckboxes={true}
          testId="accuracy-details"
          useVerticalHeaders={true}
        />
        {(<AccuracyProviderProfileFromQuery {...this.props} />)}
        <div className="mt-3">
          {(record || null) && (
            <AccuracyNetworkProvidersPanel
              accuracyTemplate={accuracyTemplate}
              {...this.props}
              inspectionBand="Unknown"
              inspectionUid={null/* TODO */}
              networkAssessmentAnalysisType={null/* TODO */}
              projectURL={this.props.location.pathname + this.props.location.search}
            />
          )}
        </div>
      </>
    );
  }

  private onPageChange = async (newPageNumber : number) => {
    await this.fetchRecords(this.state.sortedColumn, newPageNumber);
  }

  private fetchRecords = async (sortedColumn: keyof AccuracyDetailWaffleToppings = this.state.sortedColumn, newPageNumber?: number) => {
    if (!this.props.regionFilters.county && this.props.perspective !== ProjectPerspective.BySpecialty) {
      return;
    }

    const runConfiguration = this.props.runConfiguration;
    const regionFilters = this.props.regionFilters;
    const county = runConfiguration.getCounty(regionFilters.county);

    // Track the parents of regions so we can print state names in the table
    const parentRegions = new Map<string, StateName>();

    const skip: number = newPageNumber ? (newPageNumber - 1) * this.recordsPerPage : this.state.skip;
    const take = this.recordsPerPage;

    const [accuracyDetails, totalAggregateCount] = await runConfiguration.getAccuracyDetails(county, null, this.props.enabledFeatureFlags.has(Features.ACCURACY_LARGE_PROJECT_PERFORMANCE_ENHANCEMENTS), "specialty", skip, take);

    if (accuracyDetails) {
      accuracyDetails.forEach((detail) => {
        if (parentRegions.has(detail.regionCode)) {
          const parentInfo = parentRegions.get(detail.regionCode);
          detail.parentAbbreviation = parentInfo.abbreviation;
          detail.parentDisplayName = parentInfo.displayName;
        }
      });
    }

    let sortedRecords: AccuracyDetail[] = [];
    if (sortedColumn) {
      sortedRecords = accuracyDetails;
    } else {
      sortedRecords = sortBy(accuracyDetails, ["defaultDisplayOrder", "specialtyGroupDisplayOrder"]);
    }

    this.setState({
      loading: false,
      skip,
      sortedColumn,
      sortedRecords,
      totalAggregateCount
    });
  }

  private storeInspectionsAndBands = (accuracyTemplate: AccuracyTemplate) => {
    const inspectionGroups: AccuracyTemplateInspectionGroup[] = [];
    const bands: AccuracyTemplateScoringBand[] = [];

    for (const inspectionGroup of accuracyTemplate.criteria.inspectionGroups) {
      if (!inspectionGroups.includes(inspectionGroup)) {
        inspectionGroups.push(inspectionGroup);
      }
    }

    for (const bandGroup of accuracyTemplate.scoring.bandGroups) {
      for (const band of bandGroup.bands) {
        if (!bands.includes(band)) {
          bands.push(band);
        }
      }
    }

    this.inspectionGroups = inspectionGroups;
    this.bands = bands;
  }

  private buildWaffleToppings = (records: AccuracyDetail[], accuracyTemplate: AccuracyTemplate) => {
    if (records.length === 0) {
      return [];
    }

    // Only show informational specialty groups if:
    // a) We're looking at specialty groups.
    // b) One of the specialty groups is marked as such.
    // Otherwise, don't show the indicator, it's a waste of space.
    // NOTE: This is NOT a column as we don't want to deal with its interaction with ellipses.
    const showInformationalIndicator = this.props.perspective === ProjectPerspective.BySpecialty &&
      this.props.accuracyResult.details.some(({ specialtyGroupIsInformational }) => !!specialtyGroupIsInformational);

    const renderPie = (resultGroups: AccuracyAggregateInspectionResultGroup[], inspectionGroupName: string, inspection: AccuracyTemplateInspection) => {
      const bands: ProgressBarBand[] = this.getProgressBarBands(resultGroups, inspectionGroupName, inspection);
      return bands.length > 0 ? (
        <div className="pie-chart-container">
          <TooltipWrapper
            text={
              <AccuracyTooltipLegend
                inspectionName={inspection.name}
                progressBarBands={bands}
              />
            }
            position="bottom"
          >
            <Pie segments={bands.map((band) => ({ fill: band.color, percent: band.percent / 100 }))} />
          </TooltipWrapper>
        </div>
      ) : "N/A";
    };

    const inspectionGroups: AccuracyTemplateInspectionGroup[] = accuracyTemplate ? accuracyTemplate.criteria.inspectionGroups : [];

    const waffleToppings: AccuracyDetailWaffleToppings[] = records.map((sr) => {
      const key: string = sr.key;

      const title = (
        <div key={`${key}-lead`} className="text-nowrap">
          <InformationalIndicator visible={showInformationalIndicator} isInformational={!!sr.specialtyGroupIsInformational} />
          {isPathnameRestricted(`${this.props.match.url}${sr.linkSuffix}`, this.props.user)
            ? <span>{sr.displayText}</span>
            : <>
              <Link
                to={{
                  pathname: `${this.props.match.url}${sr.linkSuffix}`,
                  search: this.props.location.search,
                }}
              >
                {sr.displayText}
              </Link>
            </>}
        </div>
      );

      const providers = formatHelper(sr.providerCount as number, NumericResultType.Count);

      const score = formatHelper(sr.accuracyIndex as number, NumericResultType.Score);

      const inspections = flatMap(inspectionGroups, (inspectionGroup) => inspectionGroup.inspections.map<InspectionNode>((inspection) => (
        {
          element: renderPie(sr.resultGroups, inspectionGroup.name, inspection),
          inspectionUID: inspection.uid,
        }
      )));

      const attestationPercent = this.props.attestationRateDays === AttestationRateDays.Ninety ? sr.ninetyDayAttestedPercent : sr.recentlyAttestedPercent;

      const formattedAttestationPercent = formatHelper(attestationPercent, NumericResultType.Percent);

      return new AccuracyDetailWaffleToppings(key, providers, score, title, inspections, formattedAttestationPercent);
    });

    return waffleToppings;
  }

  private getBulkActionBar = () => {
    const networkTypes = this.props.runConfiguration && this.props.runConfiguration.networkTypes || undefined;
    const granularity = this.getRegionGranularity();
    const exportNetworkProviderCount = this.state.selectedRecords.length > 0
      ? this.state.selectedRecords.reduce((acc, sr) => acc + sr.providerCount, 0)
      : this.state.sortedRecords.reduce((acc, sr) => acc + sr.providerCount, 0);

    const stateRegionCode = this.props.regionFilters?.state;
    const countyRegionCode = this.props.regionFilters?.county;
    const specialtyGroupCode = this.props.regionFilters?.specialty;

    const selectedRecordsRegionCodes = this.state.selectedRecords.map(r => r.regionCode);
    const selectedRecordsRegionSetIds = this.state.selectedRecords.map(r => r.regionSetId);

    let selectedRegionSetIds = this.props.countyRegionSetIds.filter(c => selectedRecordsRegionSetIds.includes(c.regionSetId) || selectedRecordsRegionCodes.includes(c.state)).map(c => c.regionSetId);
    if (selectedRegionSetIds.length === 0) {
      if (countyRegionCode) {
        selectedRegionSetIds = [parseInt(countyRegionCode)];
      } else if (stateRegionCode) {
        selectedRegionSetIds = this.props.countyRegionSetIds.filter(c => c.state === stateRegionCode).map(c => c.regionSetId);
      }
    }

    let specialtyGroupCodes = this.state.selectedRecords.map((r) => r.specialtyGroupCode).filter((s) => !!s);
    if (specialtyGroupCodes.length === 0) {
      if (specialtyGroupCode) {
        specialtyGroupCodes = [specialtyGroupCode];
      }
    }
    
    const disableRegionFilters = !!countyRegionCode;

    const availableExportTypes = [ExportType.ActionableProviders, ExportType.StatusReport, ExportType.NewProviderReport, ExportType.AllInspections];

    return (
      <span className="bulk-action-group">
        <Link
          to={{
            pathname: this.props.exportEndpoint,
            state: {
              accuracyFilters: this.props.accuracyFilters,
              activeModule: this.props.module,
              availableExportTypes,
              countyFilters: (granularity == RegionGranularity.County && selectedRecordsRegionCodes.length > 0) ? selectedRecordsRegionCodes 
                            : this.props.filters.filter(f => f.filterType == FilterType.County).map(f => f.filterValue),
              disableRegionFilters,
              exportNetworkProviderCount,
              filters: this.props.filters,
              granularity,
              hasNoPublishedRun: !this.props.project.projectRunConfigurations?.some(prc => prc.isLatestPublished && !prc.isProjected && !prc.isTermed && !prc.isProjectedTermed),
              isSelectedRunPublished: !!this.props.runConfiguration?.datePublished,
              latestPublishedRunDate: this.props.project.projectRunConfigurations?.find(prc => prc.isLatestPublished && !prc.isProjected && !prc.isTermed && !prc.isProjectedTermed)?.datePublished ?? null,
              networkTypes,
              projectResourceId: this.props.project.resource.resourceId,
              projectResourceName: this.props.project.resource.name,
              projectURL: this.props.location.pathname + this.props.location.search,
              regionSetIds: selectedRegionSetIds,
              specialtyGroupCodes,
              stateFilters:  (granularity == RegionGranularity.State && selectedRecordsRegionCodes.length > 0) ? selectedRecordsRegionCodes
                            : this.props.filters.filter(f => f.filterType == FilterType.State).map(f => f.filterValue)
            },
          }}
        >
          <QesIcon iconName={QesIcons.DownloadCloudV1} isDecorative className="align-text-top me-1" />
          Create Report
        </Link>
      </span>
    );
  }

  private getRegionGranularity = () => {
    // if a county is selected or a state is selected, report at county granularity
    if (this.props.regionFilters.county || this.props.regionFilters.state || this.props.perspective === ProjectPerspective.ByCounty) {
      return RegionGranularity.County;
    }
    // if nothing is selected, the granularity is state
    return RegionGranularity.State;
  }

  private getColumnConfig = (accuracyTemplate: AccuracyTemplate, isLowestGranularity: boolean) => {
    let inspectionGroups: AccuracyTemplateInspectionGroup[] = filterProviderClaimsActivityInspectionGroup(accuracyTemplate ? accuracyTemplate.criteria.inspectionGroups : [],
      this.props.runConfiguration.excludeProviderClaimsActivityInspector);
    inspectionGroups = filterPractitionerAtLocationConfidenceInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [],
      this.props.runConfiguration.excludeAccuracyPractitionerAtLocationInspector);
    inspectionGroups = filterDirectoryFacingInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [], !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_DIRECTORY_FACING));

    const columns: ColumnConfig = {
      providers: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "providers",
      },
      recentlyAttestedPercent: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "recentlyAttestedPercent",
      },
      score: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "score",
      },
      title: {
        displayText: "",
        hidden: isLowestGranularity,
        noResize: true,
        sticky: true,
        testId: "title",
      },
    };

    inspectionGroups.forEach((ig) => {
      ig.inspections.forEach((i) => {
        columns[`${i.uid}`] = {
          displayText: i.name,
          handleSpecialSort: this.setSelectedSortingColor,
          noResize: true,
          specialSort: this.createSpecialSortElement(i.uid),
          testId: i.uid
        };
      })
    });

    return columns;
  }

  private customSort = (records: AccuracyDetailWaffleToppings[], column: keyof AccuracyDetailWaffleToppings, direction: string) => {
    const data = [...records];
    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    const inspections: string[] = [];
    for (const ig of this.inspectionGroups) {
      for (const i of ig.inspections) {
        inspections.push(i.uid);
      }
    }

    if (this.state.sortedColumn !== column) {
      this.setState({
        sortedColumn: column,
      });
    }

    if (inspections.includes(column)) {
      // sorting inspections
      let inspectionGroupName: string;
      let inspectionUid: string;
      for (const ig of this.inspectionGroups) {
        const inspection = ig.inspections.find((i) => i.uid === column);

        if (inspection) {
          inspectionUid = inspection.uid;
          inspectionGroupName = ig.name;
          break;
        }
      }

      const sortingColor = this.getSortingColor(column);

      if (sortingColor) {
        return data.sort((a, b) => (this.getInspectionBandPercentage(a, inspectionGroupName, inspectionUid, sortingColor) > this.getInspectionBandPercentage(b, inspectionGroupName, inspectionUid, sortingColor) ? 1 : -1) * multiplier);
      } else {
        return records;
      }
    } else {
      switch (column) {
        case "providers":
        case "recentlyAttestedPercent":
        case "score":
          return numberSort(records, column, multiplier);
        case "title":
          return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
        default:
          return records;
      }
    }
  }

  private getSortingColor = (column: keyof AccuracyDetailWaffleToppings) => {
    let sortingColor: string = null;
    if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
      // if a selected sorting color exists and the last sorted column is this column,
      // use the selected sorting color stored in the state
      sortingColor = this.state.selectedSortingColor;
    } else {
      const colorMap = this.sortingColorMap.get(column);

      // if a sorting color hasn't been selected, or the last column that was sorted is not this column,
      // then select the first option from the color map
      if (colorMap) {
        sortingColor = colorMap[0];

        this.setState({
          selectedSortingColor: sortingColor,
        });
      }
    }

    return sortingColor;
  }

  private getInspectionBandPercentage = (row: AccuracyDetailWaffleToppings, inspectionGroupName: string, inspectionUid: string, sortingColor: string) => {
    const aggregateDetail = this.state.sortedRecords.find((a) => a.key === row.key);

    if (aggregateDetail) {
      const resultGroup = aggregateDetail.resultGroups.find((x) => x.groupName === inspectionGroupName);

      if (resultGroup) {
        const result = resultGroup.results.find((r) => r.inspectionUid === inspectionUid);

        if (result) {
          const bandAggregate = result.bandAggregates.find((b) => b.color === sortingColor);

          if (bandAggregate) {
            return bandAggregate.bandPercentage;
          }
        }
      }
    }

    return 0;
  }

  private setSelectedSortingColor = (column: string) => {
    // determines the next selected sorting color on circle click
    const colorMap = this.sortingColorMap.get(column);

    if (colorMap) {
      if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
        const index = colorMap.indexOf(this.state.selectedSortingColor);

        if (index === colorMap.length - 1) {
          // if the currently selected sorting color is the last in the array of possible color options,
          // set the first option in said array as the next selected sorting color
          this.setState({
            selectedSortingColor: colorMap[0],
          });
        } else {
          // otherwise, select the next option
          this.setState({
            selectedSortingColor: colorMap[index + 1],
          });
        }
      } else {
        // if no color is selected or sorting is occurring in a new column,
        // set the first option in the array of possible color options as the next selected sorting color
        this.setState({
          selectedSortingColor: colorMap[0],
        });
      }
    }
  }

  private createSpecialSortElement = (column: string) => {
    const band = this.bands.find((x) => x.bandColor === this.state.selectedSortingColor);

    let tooltipText: JSX.Element;
    if (band) {
      const sortingColorMap: string[] = this.sortingColorMap.get(column);

      if (sortingColorMap && sortingColorMap.length > 1) {
        tooltipText = (
          <>
            Sorting By: {band.name}
          </>
        );
      } else {
        tooltipText = (
          <>
            All rows are {band.name}
          </>
        );
      }
    }

    return (
      <TooltipWrapper
        text={tooltipText}
        position="bottom"
      >
        <QesIcon
          iconName={QesIcons.Circle}
          pathStyle={{ fill: this.state.selectedSortingColor, stroke: "none" }}
          isDecorative
          className="cursor-pointer align-text-top"
        />
      </TooltipWrapper>
    );
  }

  private getColumnGroups = (accuracyTemplate: AccuracyTemplate, attestationRateDays: AttestationRateDays) => {
    const perspective: ProjectPerspective = this.props.perspective;
    const title = perspective === ProjectPerspective.ByState ?
      "State" :
      perspective === ProjectPerspective.ByCounty ?
        "County" :
        perspective === ProjectPerspective.BySpecialty ?
          "Specialty Group" :
          "";

    let inspectionGroups: AccuracyTemplateInspectionGroup[] = filterProviderClaimsActivityInspectionGroup(accuracyTemplate ? accuracyTemplate.criteria.inspectionGroups : [],
      this.props.runConfiguration.excludeProviderClaimsActivityInspector);
    inspectionGroups = filterPractitionerAtLocationConfidenceInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [],
      this.props.runConfiguration.excludeAccuracyPractitionerAtLocationInspector);
    inspectionGroups = filterDirectoryFacingInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [], !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_DIRECTORY_FACING));

    const columnGroups: Array<ColumnGroup<AccuracyDetailWaffleToppings>> = [
      {
        children: ["title"],
        key: title,
        testId: title,
      },
      {
        alignment: TextAlignment.Center,
        children: ["providers"],
        key: "Providers",
        testId: "providers",
      },
      {
        alignment: TextAlignment.Center,
        children: ["score"],
        key: "Score",
        testId: "score",
      },
      ...inspectionGroups.map((x) => {
        return (
          {
            children: x.inspections.map((q) => q.uid as keyof AccuracyDetailWaffleToppings),
            key: x.name,
            testId: x.uid,
          }
        );
      }),
      {
        alignment: TextAlignment.Center,
        children: ["recentlyAttestedPercent"],
        key: "Attestation %",
        testId: "recentlyAttestedPercent",
        tooltip: `Percentage is based on a ${attestationRateDays}-day attestation rate.`,
      },
    ];

    return columnGroups;
  }

  private getProgressBarBands = (resultGroups: AccuracyAggregateInspectionResultGroup[], groupName: string, inspection: AccuracyTemplateInspection) => {
    if (resultGroups) {
      const resultGroup = resultGroups.find((r) => r.groupName === groupName);
      const result = resultGroup ? resultGroup.results.find((r) => r.inspectionUid === inspection.uid) : null;
      const bandAggregates = result && result.bandAggregates || [];

      const total = bandAggregates.reduce((sum, x) => sum + x.providerCount, 0);
      return bandAggregates.map<ProgressBarBand>((a) => {
        const percent = total > 0 ? (a.providerCount / total) * 100 : 0;
        return ({
          color: a.color,
          colorType: "hex",
          justification: a.justification,
          outOfScale: percent > 0 && percent < 0.1,
          percent: this.getBandPercent(percent),
          tooltip: a.name ? a.name : "",
        });
      });
    }
  }

  private handleCheckboxChange = (selectedKeys: string[]) => {
    const selectedRecords = this.state.sortedRecords.filter((result) => selectedKeys.includes(`${result.key}`));

    this.setState({ selectedRecords });
  }

  // if the value is too small to render force smallest increment (0.1)
  private getBandPercent = (percent: number) => {
    return percent
      ? (percent > 0 && percent < 0.1) ? 0.3 : percent // 0.3 is the smallest visible segment
      : 0;
  }
}

const WrappedAccuracyDetails = withFeatureFlagContext(withUserContext(withCountyProjectContext(AccuracyDetails)));

export default WrappedAccuracyDetails;

function SkeletonResults(props: { isComparingResults?: boolean, isLowestGranularity?: boolean, runConfiguration?: ProjectRunConfigurationResult }): JSX.Element {
  if (!props.isLowestGranularity) {
    return (
      <span className="skeleton shimmer">
        <SkeletonTable />
      </span>
    );
  }

  return (
    <span className="skeleton shimmer">
      <SkeletonTable rows={1} />

      {(!props.isComparingResults || null) && (
        <div className="specialty-group-exception-editor-trigger">
          <span className="bone">Enter&nbsp;an&nbsp;Exception</span>
        </div>
      )}

      <hr className="mt0" />
      <p className="plr20 mb30 small">
        <span className="bone">
          <QesIcon iconName={QesIcons.InfoCircle} description="Info" className="mr5 align-text-top" />
          GeographyName is a GeoClass GeoType where networks are required to cover 00% of the
          beneficiaries with access to ct SpecialtyGroup EntityType within DistancePhrase.
        </span>
      </p>

      {(!props.isComparingResults || null) && (
        <SkeletonPanel className="mt50 mb0" dark={true} title="Network Providers" transparent={true} />
      )}
      {((!props.isComparingResults && props.runConfiguration && props.runConfiguration.prospectProviderSetId) || null) && (
        <SkeletonPanel className="mb0" dark={true} title="Prospect Providers" transparent={true} />
      )}
      {((!props.isComparingResults && props.runConfiguration && props.runConfiguration.useMarketProvidersForImpact) || null) && (
        <SkeletonPanel className="mb0" dark={true} title="Market Providers" transparent={true} />
      )}
    </span>
  );
}
